import React, { useState, useEffect } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom";
import MyCount from "../../../components/MyPage/MyCount";
import LeaveMembership from "../../../components/Popup/LeaveMembership";
import { formatPhoneNumber, validateJoinPassword, validatePasswordConfirm } from "../../../utils/validation";
import DaumPostcodeEmbed from 'react-daum-postcode';
import { PASSWORD_RESET_URL, MY_INFO_EDIT_URL, AUTH_VERIFICATION_INFO_EDIT_URL } from "../../../utils/urls";
import instance from "../../../services/services";
import PopupLoginError from "../../../components/Popup/LoginError";
import useIdentityVerification from "../../../hooks/useIdentityVerification";
import { useLanguage } from "../../../hooks/useLanguage";

function EditProfile(){
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isTablet, setIsTablet] = useState(false);
  const { myInfo } = useOutletContext();
  const [leavePop, setLeavePop] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [email, setEmail] = useState(myInfo?.email || '');
  
  // 비밀번호 유효성 검사를 위한 상태 추가
  const [showErrors, setShowErrors] = useState({
    currentPw: false,
    newPw: false,
    confirmPw: false
  });
  
  // 우편번호 관련 상태 추가
  const [isPostcodeOpen, setIsPostcodeOpen] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState({
    zonecode: myInfo?.mem_zipcode || '',
    roadAddress: myInfo?.mem_address_street || '',
    detailAddress: myInfo?.mem_address_detail || ''
  });

  // 수신/비수신 초기값 설정 - mailing 또는 sms가 'Y'면 'receive'로 설정
  const [receiveConsent, setReceiveConsent] = useState(
    myInfo?.mailing === 'Y' || myInfo?.sms === 'Y' ? 'receive' : 'non-receive'
  );

  // 마케팅 동의 체크박스 상태
  const [marketingChecks, setMarketingChecks] = useState({
    marketing: myInfo?.mailing === 'Y' || myInfo?.sms === 'Y',
    email: myInfo?.mailing === 'Y',
    sms: myInfo?.sms === 'Y'
  });

  // 파일 상태 추가
  const [uploadFiles, setUploadFiles] = useState({
    identification_img: null,
    student_id_img: null,
    business_registration_img: null
  });

  // 이메일 입력 핸들러
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  // 수신 동의 핸들러
  const handleConsentChange = (e) => {
    setReceiveConsent(e.target.value);
  };

  // 마케팅 체크박스 핸들러
  const handleMarketingCheck = (type) => {
    if (type === 'marketing') {
      // 마케팅 전체 동의 체크박스를 클릭했을 때
      const newValue = !marketingChecks.marketing;
      setMarketingChecks({
        marketing: newValue,
        email: newValue,
        sms: newValue
      });
    } else {
      // 개별 체크박스(이메일, SMS)를 클릭했을 때
      const newChecks = {
        ...marketingChecks,
        [type]: !marketingChecks[type]
      };
      
      // marketing 체크박스 상태 업데이트
      newChecks.marketing = newChecks.email || newChecks.sms;
      
      setMarketingChecks(newChecks);
    }
  };

  // 우편번호 찾기 핸들러
  const handlePostcodeOpen = () => {
    setIsPostcodeOpen(true);
  };

  const handlePostcodeComplete = (data) => {
    setDeliveryAddress({
      zonecode: data.zonecode,
      roadAddress: data.roadAddress,
      detailAddress: deliveryAddress.detailAddress
    });
    setIsPostcodeOpen(false);
  };

  const handlePostcodeClose = () => {
    setIsPostcodeOpen(false);
  };

  const handleDetailAddressChange = (e) => {
    setDeliveryAddress(prev => ({
      ...prev,
      detailAddress: e.target.value
    }));
  };

  // 비수신 선택 시 체크박스 초기화
  useEffect(() => {
    if (receiveConsent === 'non-receive') {
      setMarketingChecks({
        marketing: false,
        email: false,
        sms: false
      });
    }
  }, [receiveConsent]);

  // 회원탈퇴 팝업
  const handleLeave = () => {
    setLeavePop(true);
  };

  // 모바일 체크
  useEffect(() => {
    const checkTablet = () => {
      setIsTablet(window.innerWidth < 768);
    };
    
    checkTablet();
    window.addEventListener('resize', checkTablet);
    
    return () => window.removeEventListener('resize', checkTablet);
  }, []);

  // 비밀번호 변경
  const [isChangePw, setIsChangePw] = useState(false);

  const [passwordForm, setPasswordForm] = useState({
    currentPw: '',
    newPw: '',
    confirmPw: ''
  });

  const handlePassword = (show) => {
    setIsChangePw(show);
    if (!show) {
      setPasswordForm({
        currentPw: '',
        newPw: '',
        confirmPw: ''
      });
      setPasswordError('');
      setShowErrors({
        currentPw: false,
        newPw: false,
        confirmPw: false
      });
    }
  };

  // 비밀번호 입력 핸들러
  const handlePasswordChange = (e) => {
    const { id, value } = e.target;
    const fieldName = id === 'current_pw' ? 'currentPw' : 
                      id === 'new_pw' ? 'newPw' : 'confirmPw';
    
    setPasswordForm(prev => ({
      ...prev,
      [fieldName]: value
    }));
    
    // 입력 시 해당 필드의 오류 표시 활성화
    setShowErrors(prev => ({
      ...prev,
      [fieldName === 'currentPw' ? 'currentPw' : 
        fieldName === 'newPw' ? 'newPw' : 'confirmPw']: true
    }));
  };
  
  // 비밀번호 유효성 검사 함수
  const isPasswordValid = () => {
    return validateJoinPassword(passwordForm.newPw).isValid;
  };
  
  // 비밀번호 확인 유효성 검사 함수
  const isConfirmPasswordValid = () => {
    return validatePasswordConfirm(passwordForm.newPw, passwordForm.confirmPw).isValid;
  };

  // 비밀번호 변경 요청
  const handlePasswordReset = async () => {
    // 유효성 검사
    if (!passwordForm.currentPw) {
      setIsPopupOpen(t('current_password'));
      return;
    }
    
    if (!passwordForm.newPw) {
      setIsPopupOpen(t('new_password_txt'));
      return;
    }
    
    if (!isPasswordValid()) {
      setIsPopupOpen(t('new_password_2'));
      return;
    }
    
    if (!isConfirmPasswordValid()) {
      setIsPopupOpen(t('new_password_3'));
      return;
    }
    
    try {
      const response = await instance.post(PASSWORD_RESET_URL, {
        userid: myInfo?.userid,
        current_pw: passwordForm.currentPw,
        password: passwordForm.newPw,
        repassword: passwordForm.confirmPw
      });
      
      if (response.data.code === 200) {
        // 비밀번호 변경 성공
        setIsPopupOpen({
          message: t('password_changed_txt'),
          action: 'navigate_login'
        });
      } else {
        // 비밀번호 변경 실패
        setPasswordError(response.data.message || '비밀번호 변경에 실패했습니다.');
      }
    } catch (error) {
      console.error('비밀번호 변경 오류:', error);
      setPasswordError('비밀번호 변경 중 오류가 발생했습니다.');
    }
  };

  // 파일 업로드 핸들러
  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFiles(prev => ({
        ...prev,
        [fileType]: file
      }));
    }
  };

  // 부재 시 연락처 상태 추가
  const [emergencyPhone, setEmergencyPhone] = useState(myInfo?.mem_phone2 || '');

  // 부재 시 연락처 변경 핸들러 추가
  const handleEmergencyPhoneChange = (e) => {
    setEmergencyPhone(e.target.value);
  };

  // 회원 정보 수정 요청 (파일 업로드 포함)
  const handleUpdateProfile = async () => {
    try {
      // FormData 생성
      const formData = new FormData();
      
      // 기본 데이터 추가
      formData.append('mtype_seq', myInfo?.mtype_seq);
      formData.append('email', email);
      formData.append('mem_phone2', emergencyPhone); // 부재 시 연락처 추가
      formData.append('mem_zipcode', deliveryAddress.zonecode);
      formData.append('mem_address_street', deliveryAddress.roadAddress);
      formData.append('mem_address_detail', deliveryAddress.detailAddress);
      formData.append('mailing', marketingChecks.email ? 'Y' : 'N');
      formData.append('sms', marketingChecks.sms ? 'Y' : 'N');

      // 파일 추가
      if (uploadFiles.identification_img) {
        formData.append('identification_img', uploadFiles.identification_img);
      }
      if (uploadFiles.student_id_img) {
        formData.append('student_id_img', uploadFiles.student_id_img);
      }
      if (uploadFiles.business_registration_img) {
        formData.append('business_registration_img', uploadFiles.business_registration_img);
      }

      const response = await instance.post(MY_INFO_EDIT_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.code === 200) {
        // 정보 수정 성공
        setIsPopupOpen({
          message: t('profile_modified'),
          action: 'refresh'
        });
      } else {
        // 정보 수정 실패
        setIsPopupOpen(response.data.message || '회원 정보 수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('회원 정보 수정 오류:', error);
      setIsPopupOpen('회원 정보 수정 중 오류가 발생했습니다.');
    }
  };

  const handleConfirm = () => {
    if (isPopupOpen.action === 'navigate_login') {
      navigate('/login'); // 로그인 페이지로 이동
    } else if (isPopupOpen.action === 'refresh') {
      navigate('/');
    } else if (isPopupOpen.action === 'stay_on_page') {
      window.location.reload();
    }
    setIsPopupOpen(false); // 팝업 닫기
  };
  
  const { handleIdentity } = useIdentityVerification({
    context: 'MyPageEdit'
  });

  useEffect(() => {
    // 본인인증 결과 메시지 리스너
    const handleAuthMessage = (event) => {
      if (event.data && event.data.type === 'auth_verification_success') {
        const authInfo = event.data.verificationInfo;
        
        // 이름과 전화번호 필드 직접 업데이트
        const nameInput = document.querySelector('input.readonly[value="' + myInfo?.user_name + '"]');
        const phoneInput = document.querySelector('input.readonly[value="' + formatPhoneNumber(myInfo?.mem_phone || '') + '"]');
        
        if (nameInput && authInfo.username) {
          nameInput.value = authInfo.username;
        }
        
        if (phoneInput && authInfo.mem_phone) {
          phoneInput.value = formatPhoneNumber(authInfo.mem_phone);
        }
        
        // 인증 정보로 회원정보 업데이트 함수
        const updateUserInfoWithAuth = async () => {
          try {
            // AUTH_VERIFICATION_INFO_EDIT_URL API 호출
            const response = await instance.post(AUTH_VERIFICATION_INFO_EDIT_URL, {
              username: authInfo.username,
              phone: authInfo.mem_phone,
              ci: authInfo.ci || "",
              di: authInfo.di || ""
            });
            
            if (response.data.code === 200) {
              setIsPopupOpen({
                message: t('profile_modified2'),
                action: 'stay_on_page'
              });
            } else {
              setIsPopupOpen({
                message: response.data.message || '본인 인증 정보 수정에 실패했습니다.',
                closeButton: '확인'
              });
            }
          } catch (error) {
            console.error('본인 인증 정보 수정 오류:', error);
            setIsPopupOpen({
              message: '본인 인증 정보 수정 중 오류가 발생했습니다.',
              closeButton: '확인'
            });
          }
        };
        
        // 업데이트 함수 호출
        updateUserInfoWithAuth();
      }
    };
    
    window.addEventListener('message', handleAuthMessage);
    
    return () => {
      window.removeEventListener('message', handleAuthMessage);
    };
  }, [myInfo, t]);

  // myInfo가 로드되면 상태 업데이트하는 useEffect에 emergencyPhone 추가
  useEffect(() => {
    if (myInfo) {
      setEmail(myInfo.email || '');
      setEmergencyPhone(myInfo.mem_phone2 || ''); // 부재 시 연락처 추가
      setDeliveryAddress({
        zonecode: myInfo.mem_zipcode || '',
        roadAddress: myInfo.mem_address_street || '',
        detailAddress: myInfo.mem_address_detail || ''
      });
      setReceiveConsent(
        myInfo.mailing === 'Y' || myInfo.sms === 'Y' ? 'receive' : 'non-receive'
      );
      setMarketingChecks({
        marketing: myInfo.mailing === 'Y' || myInfo.sms === 'Y',
        email: myInfo.mailing === 'Y',
        sms: myInfo.sms === 'Y'
      });
    }
  }, [myInfo]);

  return(
    <div>
      <div>
        {/* 장바구니 / 관심상품 / 쿠폰 */}
        {!isTablet && <MyCount/>}

        <div className="my_container profile_container mt_48">
          <div className="order_title">
            <h3>{t('member_info_2')}</h3>
          </div>

          <ul className="default_info_table v2 pt_0 edit_profile_table">
            {/* 회원유형 */}
            <li className="info_item flex-nowrap border_item">
              <div className="label">
                <span>{t('member_type')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail">
                    {myInfo?.mem_kind_txt}
                  <span 
                    className="info_detail_small"
                    onClick={handleLeave}
                  >
                    {t('withdrawal_completed_message_12')}
                  </span>
                </div>
                
              </div>
            </li>

            {/* 아이디 */}
            <li className="info_item flex-nowrap border_item">
              <div className="label">
                <span>{t('id')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  {myInfo?.userid}
                </div>
              </div>
            </li>

            {/* 비밀번호 변경 */}
            <li className="info_item change_box">
              <div className="label">
                <span>{t('change_password2')}</span>
              </div>
              <div className="form_info change_password">
                {passwordError && <p style={{color:'red'}}>{passwordError}</p>}
                <div className="info_detail">
                  {!isChangePw ? (
                    <button
                      className="default_btn light_gray sm w-100 w-half"
                      type="button"
                      onClick={() => handlePassword(true)}
                    >
                      {t('change_password2')}
                    </button>
                  ) : (
                    <div className="change_password w-half">
                      <div className="form_group">
                        <div className="default_input flex-8 column">
                          <label htmlFor="current_pw">
                            {t('withdrawal_completed_message_24')} <i className="red">*</i>
                          </label>
                          <div>
                            <input
                              type="password"
                              id="current_pw"
                              value={passwordForm.currentPw}
                              onChange={handlePasswordChange}
                              placeholder={t('withdrawal_completed_message_25')}
                            />
                          </div>
                        </div>
                        <div className="default_input flex-8 column">
                          <label htmlFor="new_pw">
                            {t('new_password')} <i className="red">*</i>
                          </label>
                          <div>
                            <input
                              id="new_pw"
                              type="password"
                              value={passwordForm.newPw}
                              onChange={handlePasswordChange}
                              placeholder={t('password_rule')}
                            />
                            {showErrors.newPw && !isPasswordValid() && (
                              <span className="guide_text red">
                                {t('password_rule_instruction')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="default_input flex-8 column">
                          <label htmlFor="confirm_pw">
                            {t('new_password_confirm')} <i className="red">*</i>
                          </label>
                          <div>
                            <input
                              id="confirm_pw"
                              type="password"
                              value={passwordForm.confirmPw}
                              onChange={handlePasswordChange}
                              placeholder={t('reenter_new_password')}
                            />
                            {showErrors.confirmPw && !isConfirmPasswordValid() && (
                              <span className="guide_text red">
                                {t('password_mismatch')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="btn_group w-half">
                        <button
                          className="default_btn light_gray sm"
                          type="button"
                          onClick={() => handlePassword(false)}
                        >
                          {t('cancel')}
                        </button>
                        <button
                          className="default_btn black sm"
                          type="button"
                          onClick={handlePasswordReset}
                        >
                          {t('withdrawal_completed_message_26')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </li>

            {/* 회원정보 */}
            <li className="info_item">
              <div className="label">
                <span>{t('member_info')}<br />{t('withdrawal_completed_message_14')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail default_input flex-8 column">
                  <input
                    type="text"
                    className="readonly w-half"
                    value={myInfo?.user_name || ''}
                    readOnly
                  />
                  <input
                    type="text"
                    className="readonly w-half"
                    value={formatPhoneNumber(myInfo?.mem_phone || '')}
                    readOnly
                  />
                  <button
                    className="default_btn black sm w-half"
                    type="button"
                    onClick={handleIdentity}
                  >
                    {t('withdrawal_completed_message_15')}
                  </button>
                </div>
              </div>
            </li>

            {/* 부재 시 연락처 */}
            <li className="info_item">
              <div className="label">
                <label htmlFor="emergency_phone">{t('alternate_contact')}</label>
              </div>
              <div className="form_info">
                <div className="info_detail default_input">
                  <input
                    type="text"
                    className="w-half"
                    id="emergency_phone"
                    value={emergencyPhone}
                    onChange={handleEmergencyPhoneChange}
                    placeholder="부재 시 연락처를 입력해주세요"
                  />
                </div>
              </div>
            </li>

            {/* 성별 */}
            <li className="info_item flex-nowrap border_item">
              <div className="label">
                <span>{t('gender')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  {myInfo?.mem_gender === "M" ? t('male') : t('female')}
                </div>
              </div>
            </li>

            {/* 생년월일 */}
            <li className="info_item flex-nowrap border_item">
              <div className="label">
                <span>{t('birthdate')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  {myInfo?.mem_birthday}
                </div>
              </div>
            </li>

            {/* 주민등록번호 */}
            <li className="info_item flex-nowrap border_item">
              <div className="label">
                <span>{t('resident_number')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  {myInfo?.jumin}-{myInfo?.jumin2}******
                </div>
              </div>
            </li>

            {/* 이메일 */}
            <li className="info_item">
              <div className="label">
                <label htmlFor="user_email">{t('email')}</label>
              </div>
              <div className="form_info">
                <div className="info_detail default_input">
                  <input
                    type="text"
                    className="w-half"
                    id="user_email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder={t('email_input')}
                  />
                </div>
              </div>
            </li>

            {/* 신분증 */}
            <li className="info_item">
              <div className="label">
                <span>{t('id_card')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail">
                  <div className="default_input flex-center-4  zip_code">
                    <input
                      id="identificationFile"
                      type="file"
                      accept="image/*, .pdf"
                      onChange={(e) => handleFileChange(e, 'identification_img')}
                      style={{ display: 'none' }}
                      disabled={myInfo?.agree_flag !== 'R'}
                    />
                    <span className="form_input placeholder disabled">
                      <span className="ellipsis_clamp">
                        {uploadFiles.identification_img?.name || myInfo?.identification_img_name || '파일을 선택해주세요'}
                      </span>
                    </span>
                    <label
                      htmlFor="identificationFile"
                      className={`default_btn sm ${myInfo?.agree_flag !== 'R' ? 'light_gray disabled' : 'light_gray'}`}
                      style={myInfo?.agree_flag !== 'R' ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                    >
                      {t('withdrawal_completed_message_16')}
                    </label>
                  </div>
                </div>
              </div>
            </li>
            {myInfo?.mtype_seq === '2' && (
              <>
                {/* 학생증 */}
                <li className="info_item">
                  <div className="label">
                    <span>{t('student_id')}</span>
                  </div>
                  <div className="form_info">
                    <div className="info_detail">
                      <div className="default_input flex-center-4  zip_code">
                        <input
                          id="studentIdFile"
                          type="file"
                          accept="image/*, .pdf"
                          onChange={(e) => handleFileChange(e, 'student_id_img')}
                          style={{ display: 'none' }}
                          disabled={myInfo?.agree_flag !== 'R'}
                        />
                        <span className="form_input placeholder disabled">
                          <span className="ellipsis_clamp">
                            {uploadFiles.student_id_img?.name || myInfo?.student_id_img_name || '파일을 선택해주세요'}
                          </span>
                        </span>
                        <label
                          htmlFor="studentIdFile"
                          className={`default_btn sm ${myInfo?.agree_flag !== 'R' ? 'light_gray disabled' : 'light_gray'}`}
                          style={myInfo?.agree_flag !== 'R' ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                        >
                          {t('withdrawal_completed_message_16')}
                        </label>
                      </div>
                    </div>
                  </div>
                </li>
              </>
            )}
            {/* {myInfo?.mtype_seq === '4' && ( */}
            {myInfo?.member_kind ==='corporate' && (
              <>
                {/* 회사명 */}
                <li className="info_item flex-nowrap border_item">
                  <div className="label">
                    <span>{t('company_name')}</span>
                  </div>
                  <div className="form_info">
                    <div className="info_detail">
                      {myInfo?.business_name}
                    </div>
                  </div>
                </li>
                {/* 사업자등록번호 */}
                <li className="info_item flex-nowrap border_item">
                  <div className="label">
                    <span>{t('business_registration_number_1')}</span>
                  </div>
                  <div className="form_info">
                    <div className="info_detail">
                      {myInfo?.business_num}
                    </div>
                  </div>
                </li>
                {/* 대표자명 */}
                <li className="info_item flex-nowrap border_item">
                  <div className="label">
                    <span>{t('ceo_name')}</span>
                  </div>
                  <div className="form_info">
                    <div className="info_detail">
                      {myInfo?.business_ceo}
                    </div>
                  </div>
                </li>
                {/* 업태 */}
                <li className="info_item flex-nowrap border_item">
                  <div className="label">
                    <span>{t('industry_1')}</span>
                  </div>
                  <div className="form_info">
                    <div className="info_detail">
                      {myInfo?.business_category}
                    </div>
                  </div>
                </li>
                {/* 종목 */}
                <li className="info_item flex-nowrap border_item">
                  <div className="label">
                    <span>{t('business_registration_number_2')}</span>
                  </div>
                  <div className="form_info">
                    <div className="info_detail">
                      {myInfo?.business_item}
                    </div>
                  </div>
                </li>
                {/* 사업장 소재지 */}
                <li className="info_item flex-nowrap border_item">
                  <div className="label">
                    <span>{t('business_address')}</span>
                  </div>
                  <div className="form_info">
                    <div className="info_detail flex-8 column">
                      <p>
                        [{myInfo?.business_zipcode}]
                      </p>
                      <p>
                        {myInfo?.business_address_street}
                      </p>
                      <p>
                        {myInfo?.business_address_detail}
                      </p>
                    </div>
                  </div>
                </li>
                {/* 사업자등록증 */}
                <li className="info_item">
                  <div className="label">
                    <span>{t('business_registration_certificate')}</span>
                  </div>
                  <div className="form_info">
                    <div className="info_detail">
                      <div className="default_input flex-center-4  zip_code">
                        <input
                          id="businessRegistrationFile"
                          type="file"
                          accept="image/*, .pdf"
                          onChange={(e) => handleFileChange(e, 'business_registration_img')}
                          style={{ display: 'none' }}
                          disabled={myInfo?.agree_flag !== 'R'}
                        />
                        <span className="form_input placeholder disabled">
                          <span className="ellipsis_clamp">
                            {uploadFiles.business_registration_img?.name || myInfo?.business_registration_img_name || '파일을 선택해주세요'}
                          </span>
                        </span>
                        <label
                          htmlFor="businessRegistrationFile"
                          className={`default_btn sm ${myInfo?.agree_flag !== 'R' ? 'light_gray disabled' : 'light_gray'}`}
                          style={myInfo?.agree_flag !== 'R' ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                        >
                          {t('withdrawal_completed_message_16')}
                        </label>
                      </div>
                    </div>
                  </div>
                </li>
              </>
            )}

            {/* 주소 */}
            <li className="info_item">
              <div className="label">
                <span>{t('delivery_address')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail default_input">
                  <div className="address_input">
                    <div className="zip_code flex-center-4">
                      <input 
                        type="text" 
                        value={deliveryAddress.zonecode}
                        readOnly 
                      />
                      <button 
                        type="button" 
                        className="default_btn sm light_gray"
                        onClick={handlePostcodeOpen}
                      >
                        {t('postal_code_search')}
                      </button>
                    </div>
                    <input 
                      type="text" 
                      value={deliveryAddress.roadAddress}
                      readOnly 
                    />
                    <input 
                      type="text" 
                      placeholder={t('delivery_address_name9')} 
                      value={deliveryAddress.detailAddress}
                      onChange={handleDetailAddressChange}
                    />
                  </div>
                </div>
              </div>
            </li>

            {/* 이벤트 정보 */}
            <li className="info_item">
              <div className="label">
                <span>{t('withdrawal_completed_message_17')}</span>
              </div>
              <div className="form_info">
                <div className="info_detail flex-12 column">
                  <div className="default_radio radio_list">
                    <div>
                      <input 
                        type="radio" 
                        name="consent" 
                        id="receive" 
                        value="receive" 
                        checked={receiveConsent === 'receive'}
                        onChange={handleConsentChange}
                      />
                      <label htmlFor="receive">{t('withdrawal_completed_message_27')}</label>
                    </div>
                    <div>
                      <input
                        type="radio" 
                        name="consent" 
                        id="non_receive" 
                        value="non-receive" 
                        checked={receiveConsent === 'non-receive'}
                        onChange={handleConsentChange}
                      />
                      <label htmlFor="non_receive">{t('withdrawal_completed_message_28')}</label>
                    </div>
                  </div>

                  {receiveConsent === 'receive' && (
                    <>
                      <div className="alert_box gray input_list">
                        <div className="default_chk_box">
                          <input 
                            type="checkbox" 
                            id="marketing_consent" 
                            checked={marketingChecks.marketing}
                            onChange={() => handleMarketingCheck('marketing')}
                          />
                          <label htmlFor="marketing_consent">
                            <span className="terms_btn flex-8">
                              {t('withdrawal_completed_message_18')}
                              <Link 
                                to="/marketing-agreement"
                                className="color_g80 text_underline" 
                              >{t('view')}
                              </Link>
                            </span>
                          </label>
                        </div>
                        <div className="default_chk_box">
                          <input 
                            type="checkbox" 
                            id="email_agree" 
                            checked={marketingChecks.email}
                            onChange={() => handleMarketingCheck('email')}
                          />
                          <label htmlFor="email_agree">{t('email')}</label>
                        </div>
                        <div className="default_chk_box">
                          <input 
                            type="checkbox" 
                            id="sms_agree" 
                            checked={marketingChecks.sms}
                            onChange={() => handleMarketingCheck('sms')}
                          />
                          <label htmlFor="sms_agree">SMS</label>
                        </div>
                      </div>

                      <div className="dash_list flex-2 column">
                        <span className="guide_text_1 g-80">
                          {t('withdrawal_completed_message_19')}
                        </span>
                        <span className="guide_text_1 g-80">
                          {t('withdrawal_completed_message_20')}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </li>
          </ul>
        </div>

        <div className="submit_box mt_48 bg">
          <button 
            className="default_btn lg black" 
            type="button"
            onClick={handleUpdateProfile}
          >
            {t('withdrawal_completed_message_22')}
          </button>
        </div>
      </div>

      {/* 회원탈퇴 팝업 */}
      <LeaveMembership isPopup={leavePop} setIsPopup={setLeavePop}/>
      
      {/* 우편번호 모달 */}
      {isPostcodeOpen && (
        <>
          <div 
            className="dimmed" 
            onClick={handlePostcodeClose}
            style={{
              position: 'fixed',
              width: '100vw',
              height: '100vh',
              top: '0',
              left: '0',
              background: 'rgba(0, 0, 0, 0.50)',
              zIndex: 101
            }}
          />
          <div className="default_popup">
            <div className="modal_content">
              <div className="modal_wrap">
                <div className="modal_header">
                  <button 
                    type="button" 
                    className="close_btn" 
                    onClick={handlePostcodeClose}
                  ></button>
                </div>
                <div className="modal_body">
                  <DaumPostcodeEmbed
                    onComplete={handlePostcodeComplete}
                    onClose={handlePostcodeClose}
                    style={{ height: '460px' }}
                  />
                </div>
              </div>
            </div>

          </div>
        </>
      )}
      <PopupLoginError isPopup={isPopupOpen} setIsPopup={setIsPopupOpen} onConfirm={handleConfirm}/>

        {/* NICE 인증을 위한 숨겨진 폼 */}
        <form 
          id="niceAuthForm" 
          name="niceAuthForm" 
          action="https://nice.checkplus.co.kr/CheckPlusSafeModel/checkplus.cb"
          method="post"
          style={{ display: 'none' }}
      >
          <input type="hidden" id="m" name="m" value="service" />
          <input type="hidden" id="token_version_id" name="token_version_id" value="" />
          <input type="hidden" id="enc_data" name="enc_data" value="" />
          <input type="hidden" id="integrity_value" name="integrity_value" value="" />
          <input type="hidden" id="receivedata" name="receivedata" value="" />
      </form>

    </div>
  )
};

export default EditProfile;